import React, { useMemo, useState } from "react";
import SlideOutSidebar from "../../../ui-components/slide-out-sidebar/slideOutSidebar";
import Button, { ButtonThemes } from "../../../ui-components/button/button";
import LabeledTextInput from "../../../ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import LabeledSelectInput from "../../../ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import LabeledDateRangeInput from "../../../ui-components/molecules/labeled-inputs/labeled-date-range-input/labeledDateRangeInput";
import { LabelModes } from "../../../ui-components/molecules/labeled-inputs/labeledInput";
import Table from "../../../ui-components/table/table";
import { XGSSelectOption } from "../../../ui-components/xgs-select/xgsSelect";
import XGSErrorMessage from "../../../ui-components/error-message/errorMessage";
import {
  EQUIPMENT_ACTIVITY_LOG_ACTION_OPTIONS,
  EquipmentActivityLogModel,
} from "../../../app/data/equipment/models";
import {
  EquipmentActivityLogFilters,
  useEquipmentActivityLogList,
} from "../../../hooks/useEquipment";
import getEquipmentActivityLogColumns from "./equipmentActivityColumns";
import "./equipmentActivityLog.scss";

export interface EquipmentActivityLogProps {
  show: boolean;
  onClose: () => void;
}

const formatFilterDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "";
  const parts = dateStr.split("/");
  if (parts.length !== 3) return "";
  const [month, day, year] = parts;
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
};

const formatDescriptionLine = (line: string): string => {
  const updatedLine = line.replace(/-->/g, "→");
  return updatedLine.replace(
    /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z\[UTC\]/g,
    (match) => {
      const clean = match.replace(/\[.*\]/, "");
      return typeof (clean as any).toUiMonthDateFormat === "function"
        ? (clean as any).toUiMonthDateFormat()
        : clean;
    }
  );
};

const EquipmentActivityLog: React.FC<EquipmentActivityLogProps> = (props) => {
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [actionType, setActionType] = useState<XGSSelectOption | null | undefined>(null);
  const [appliedFilters, setAppliedFilters] = useState<EquipmentActivityLogFilters>({});

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError,
    error,
  } = useEquipmentActivityLogList(appliedFilters, props.show);

  const actionOptions = useMemo(
    () =>
      EQUIPMENT_ACTIVITY_LOG_ACTION_OPTIONS.map(
        (action): XGSSelectOption => ({
          label: action.name,
          value: action.id,
        })
      ),
    []
  );

  const tableRows = useMemo(() => {
    const logs: EquipmentActivityLogModel[] = data?.allItems || [];
    const rows: Array<{
      changedAt: string;
      equipmentLabel: string;
      equipmentType: string | null;
      action: string;
      modifiedBy: string;
      changeDescription: string;
    }> = [];

    logs.forEach((item) => {
      const lines = (item.changeDescription || "")
        .split("\n")
        .filter((line) => line.trim() !== "");

      const equipmentLabel =
        item.unitNumber ||
        item.equipmentId ||
        [item.location, item.equipmentType].filter(Boolean).join(" - ") ||
        "—";

      if (lines.length === 0) {
        rows.push({
          changedAt: item.changedAt,
          equipmentLabel,
          equipmentType: item.equipmentType,
          action: item.action,
          modifiedBy: item.modifiedBy,
          changeDescription: "",
        });
        return;
      }

      lines.forEach((line) => {
        rows.push({
          changedAt: item.changedAt,
          equipmentLabel,
          equipmentType: item.equipmentType,
          action: item.action,
          modifiedBy: item.modifiedBy,
          changeDescription: formatDescriptionLine(line),
        });
      });
    });

    return rows;
  }, [data?.allItems]);

  const searchRecords = () => {
    setAppliedFilters({
      search: search.trim() || undefined,
      action: actionType?.value || actionType?.label || null,
      dateFrom: fromDate || null,
      dateTo: toDate || null,
    });
  };

  const clearSearchRecords = () => {
    setFromDate("");
    setToDate("");
    setSearch("");
    setActionType(null);
    setAppliedFilters({});
  };

  return (
    <SlideOutSidebar
      header="Equipment Activity Log"
      onClose={props.onClose}
      show={props.show}
      size="medium"
      className="xgs-equipment-activitylog"
    >
      <>
        <div className="xgs-list__controls">
          <div className="xgs-list__controls__search xgs-equipment-activitylog__filters">
            <LabeledTextInput
              label="Search Equipment"
              labelMode={LabelModes.column}
              className="xgs-equipment-activitylog__filters__input"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              type="text"
              placeholder="Search across all fields"
            />
            <LabeledDateRangeInput
              label="Dates range:"
              labelMode={LabelModes.column}
              className="xgs-equipment-activitylog__filters__dates"
              start={fromDate}
              end={toDate}
              onStartChange={(value) => setFromDate(formatFilterDate(value))}
              onEndChange={(value) => setToDate(formatFilterDate(value))}
            />
            <LabeledSelectInput
              label="Action"
              onValueChange={setActionType}
              name="actionType"
              value={actionType}
              className="xgs-equipment-activitylog__filters__input"
              options={actionOptions}
              labelMode={LabelModes.column}
              placeholder="Action Type"
            />
            <Button
              className="xgs-equipment-activitylog__filters__button"
              theme={ButtonThemes.gray}
              onClick={clearSearchRecords}
            >
              Clear Filters
            </Button>
            <Button
              className="xgs-equipment-activitylog__filters__button xgs-equipment-activitylog__filters__button--last"
              theme={ButtonThemes.blue}
              onClick={searchRecords}
            >
              Search
            </Button>
          </div>
        </div>

        {isError && (
          <XGSErrorMessage>
            {(error as any)?.message || "Error while obtaining the activity log"}
          </XGSErrorMessage>
        )}

        {!isError && (
          <div className="xgs-equipment-activitylog__table-wrapper">
            <Table
              isLoading={isLoading}
              columns={getEquipmentActivityLogColumns()}
              data={tableRows}
              rowHeight={50}
              minTableHeight={200}
              noResultsText="There are no logs available"
              infiniteScroll
              infiniteScrollLoading={isFetchingNextPage}
              infiniteScrollHasNext={!!hasNextPage}
              onInfiniteScroll={() => {
                if (hasNextPage && !isFetchingNextPage) {
                  fetchNextPage();
                }
              }}
              sorting={true}
              enableMultiSort={true}
            />
          </div>
        )}
      </>
    </SlideOutSidebar>
  );
};

export default EquipmentActivityLog;
